/**
 * Thin delegator to the single global photoOrg.zuulAuthClient (see
 * app/assets/javascripts/common/utils/zuul_auth_client.js). The Vite/TS bundles and the legacy
 * Sprockets bundles both need the same Bearer/OAuth header decision, so the actual logic lives
 * once, on window.photoOrg - this class just gives the TS side a typed entry point into it,
 * matching how other TS code already reads photoOrg.* globals directly (e.g. public_share_module).
 */
declare const photoOrg: any;

export class ZuulAuthClient {

    static isEnabled(): boolean {
        return typeof photoOrg !== 'undefined' && !!photoOrg.zuulAuthClient && photoOrg.zuulAuthClient.isEnabled();
    }

    /**
     * Builds the Authorization header value from a token and a mode flag.
     * Determines the prefix ("Bearer" for Zuul/JWT id-tokens, "OAuth" for legacy oa2 tokens)
     * based on isZuulMode so the prefix string is never hardcoded at individual call sites.
     * Delegates to the JS-side zuulAuthClient.buildAuthHeaderValue() when available so the
     * prefix logic lives in exactly one place (zuul_auth_client.js).
     *
     * @param token      - The token value (id token or legacy oa2 token).
     * @param isZuulMode - true when Zuul is enabled (use "Bearer"), false for legacy (use "OAuth").
     */
    static buildAuthHeaderValue(token: string, isZuulMode: boolean): string {
        if (typeof photoOrg !== 'undefined' && photoOrg.zuulAuthClient && photoOrg.zuulAuthClient.buildAuthHeaderValue) {
            return photoOrg.zuulAuthClient.buildAuthHeaderValue(token, isZuulMode);
        }
        // Fallback: mirror the same prefix logic inline so this method is always safe to call
        // even before the Sprockets bundle has initialised photoOrg.zuulAuthClient.
        const prefix = isZuulMode ? "Bearer" : "OAuth";
        return `${prefix} ${token}`;
    }

    static getAuthHeaderValue(legacyToken: string): string | undefined {
        if (typeof photoOrg === 'undefined' || !photoOrg.zuulAuthClient) {
            // zuulAuthClient not yet initialised - fall back to legacy OAuth prefix via helper
            return ZuulAuthClient.buildAuthHeaderValue(legacyToken, false);
        }
        return photoOrg.zuulAuthClient.getAuthHeaderValue(legacyToken);
    }

    /**
     * The account id (payload.uid) from the cached Zuul token, used to build the AcctId header
     * so the backend can cross-check the account id in the header against the one in the token.
     * Returns undefined in legacy mode (or before zuulAuthClient has initialised) - callers
     * should treat that the same as "don't send the header".
     */
    static getAcctIdHeaderValue(): string | undefined {
        if (typeof photoOrg === 'undefined' || !photoOrg.zuulAuthClient) {
            return undefined;
        }
        return photoOrg.zuulAuthClient.getAcctIdHeaderValue();
    }

    static refreshIdToken(): Promise<string | null> {
        if (typeof photoOrg === 'undefined' || !photoOrg.zuulAuthClient) {
            return Promise.resolve(null);
        }
        return photoOrg.zuulAuthClient.refreshIdToken();
    }
}
